import React, { useContext } from 'react';
import styled, { css } from 'styled-components';
import { GetColor, Headline3, Icon } from 'venn-ui-kit';
import { DelimitedWrapper, GapWrapper, type ImageProps, type RCMCustomizationProps, StyledLabel } from '../shared';
import PageSection from './PageSection';
import { dimensions, type Page, UserContext } from 'venn-components';
import { isReportState, studioPrintOrientationType, viewPages } from 'venn-state';
import { atom, DefaultValue, selector, useRecoilCallback, useRecoilValue } from 'recoil';
import TitlePageSettingsSection from './TitlePageSettingsSection';
import SectionPageSettingsSection from './SectionPageSettingsSection';
import { useHasFF } from 'venn-utils';
import type { PrintOrientationTypeEnum } from 'venn-api';
import type { Layout } from 'react-grid-layout';

type OrientationLayoutHistory = Partial<Record<PrintOrientationTypeEnum, Layout[]>>;

const orientationLayoutHistoryState = atom<OrientationLayoutHistory>({
  key: 'orientationLayoutHistory',
  default: {},
});

type OrientationSwitch = { history: OrientationLayoutHistory; orientation: PrintOrientationTypeEnum; pages: Page[] };

const orientationSwitchState = selector<OrientationSwitch>({
  key: 'orientationSwitch',
  get: ({ get }) => ({
    history: get(orientationLayoutHistoryState),
    orientation: get(studioPrintOrientationType),
    pages: get(viewPages),
  }),
  set: ({ set }, value) => {
    if (value instanceof DefaultValue) return;
    set(orientationLayoutHistoryState, value.history);
    set(studioPrintOrientationType, value.orientation);
    set(viewPages, value.pages);
  },
});

const ReportingSection = () => {
  const hasCustomizationEnhancements = useHasFF('rl_customization_enhancements_ff');
  const { hasPermission } = useContext(UserContext);
  const isReport = useRecoilValue(isReportState);
  const printOrientationType = useRecoilValue(studioPrintOrientationType);

  const setPrintOrientationType = useRecoilCallback(
    ({ set, snapshot }) =>
      (newOrientation: PrintOrientationTypeEnum) => {
        const currentOrientation = snapshot.getLoadable(studioPrintOrientationType).getValue();
        if (currentOrientation === newOrientation) {
          return;
        }

        const { noRows, noColumns } = dimensions[newOrientation].gridSettings;
        const history = snapshot.getLoadable(orientationLayoutHistoryState).getValue();
        const newOrientationLayout = history[newOrientation];
        const currentOrientationLayout = history[currentOrientation];
        const pages = snapshot.getLoadable(viewPages).getValue();

        const newPages = pages.map((page, pageIndex) => {
          const newOrientationPageLayout = newOrientationLayout?.[pageIndex];
          const currentOrientationPageLayout = currentOrientationLayout?.[pageIndex];
          const pageBlockIds = page.layout.map((item) => item.i).sort();
          const newOrientationBlockIds = newOrientationPageLayout?.map((item) => item.i).sort();
          const sizesChanged =
            currentOrientationPageLayout !== undefined &&
            page.layout.some(
              (item) => !currentOrientationPageLayout.find((l) => l.i === item.i && l.w === item.w && l.h === item.h),
            );
          const isNewLayoutBlocksObsolete =
            newOrientationPageLayout === undefined ||
            newOrientationBlockIds?.length !== pageBlockIds.length ||
            !newOrientationBlockIds.every((id, idx) => id === pageBlockIds[idx]);

          if (!sizesChanged && !isNewLayoutBlocksObsolete) {
            return { ...page, layout: newOrientationPageLayout };
          }

          const maxBottomRow = page.layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
          const maxRightCol = page.layout.reduce((max, item) => Math.max(max, item.x + item.w), 0);

          const scaleH = maxBottomRow > noRows ? noRows / maxBottomRow : 1;
          const scaleW = maxRightCol > noColumns ? noColumns / maxRightCol : 1;

          return {
            ...page,
            layout: page.layout.map(({ i, w, h, x, y, ...rest }) => ({
              ...rest,
              i,
              w: Math.max(1, Math.round(w * scaleW)),
              x: Math.round(x * scaleW),
              h: Math.max(1, Math.round(h * scaleH)),
              y: Math.round(y * scaleH),
            })),
          };
        });

        set(orientationSwitchState, {
          history: {
            ...history,
            [currentOrientation]: pages.map((p) => p.layout),
            [newOrientation]: newPages.map((p) => p.layout),
          },
          orientation: newOrientation,
          pages: newPages,
        });
      },
    [],
  );

  const canCreateTemplate = hasPermission('STUDIO_CREATE_TEMPLATE');

  const Wrapper = hasCustomizationEnhancements ? DelimitedWrapper : GapWrapper;

  return (
    <Wrapper>
      <Headline3>Report Settings</Headline3>
      <StyledLabel htmlFor="print-orientation">Orientation</StyledLabel>
      <IconSelectionWrapper>
        <IconSelection>
          <IconLabelStack>
            <ReportingIcon
              type="rectangle-portrait"
              prefix="fad"
              selected={printOrientationType === 'PORTRAIT'}
              onClick={!canCreateTemplate ? undefined : () => setPrintOrientationType('PORTRAIT')}
              disabled={!canCreateTemplate}
              isRcmCustomization={hasCustomizationEnhancements}
              left
            />
            <IconLabel customFontSize={hasCustomizationEnhancements ? '12px' : undefined}>Portrait</IconLabel>
          </IconLabelStack>
        </IconSelection>
        <IconSelection>
          <IconLabelStack>
            <ReportingIcon
              type="rectangle-portrait"
              prefix="fad"
              selected={printOrientationType === 'LANDSCAPE'}
              onClick={!canCreateTemplate ? undefined : () => setPrintOrientationType('LANDSCAPE')}
              disabled={!canCreateTemplate}
              isRcmCustomization={hasCustomizationEnhancements}
              rotated
            />
            <IconLabel customFontSize={hasCustomizationEnhancements ? '12px' : undefined}>Landscape</IconLabel>
          </IconLabelStack>
        </IconSelection>
      </IconSelectionWrapper>

      {isReport && (
        <>
          <PageSection />
          <TitlePageSettingsSection />
          <SectionPageSettingsSection />
        </>
      )}
    </Wrapper>
  );
};

export default ReportingSection;

const IconSelectionWrapper = styled.div`
  display: table;
  width: fit-content;
`;

const IconLabelStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: fit-content;
  margin: 5px;
  margin-right: 25px;
`;

const ReportingIcon = styled(Icon)<ImageProps & RCMCustomizationProps>`
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  border: ${(props) =>
    props.selected
      ? css`
        3px solid ${props.isRcmCustomization ? GetColor.DarkBlue : GetColor.Primary.Main}
      `
      : '0px'};
  border-radius: ${(props) => (props.isRcmCustomization ? '10px' : '0')};
  font-size: 50px;
  margin: 0px;
  color: orange;
  display: table-cell;
  float: left;
  white-space: nowrap;
  width: max-content;
  transform: ${(props) => (props.rotated ? 'rotate(90deg)' : 'none')};
`;

const IconSelection = styled.div`
  display: table-cell;
`;

const IconLabel = styled.div<{ customFontSize?: string }>`
  margin: 5px;
  font-size: ${({ customFontSize }) => customFontSize || 'inherit'};
`;
